import { useCallback, useEffect, useRef } from 'react';

export class StageRefreshFehler extends Error {
  constructor(public art: 'http' | 'zeitgrenze' | 'daten' | 'ansicht' | 'verbindung', text: string) { super(text); this.name='StageRefreshFehler'; }
}
/** Only controlled messages reach the UI; boundary bodies and request URLs may contain private data. */
export function stageRefreshHinweis(error: unknown): string {
  return error instanceof StageRefreshFehler ? error.message : 'Die Aktualisierung konnte nicht bestätigt werden.';
}

/** Read-only refresh: callback renders must not cancel a pending retry or reset
 * the safety interval. A changed stage invalidates the old response. */
export function useStageRefresh<T>({ id, haus, read, onData, onError }: {
  id?: string; haus?: string;
  read: (id: string) => Promise<T>;
  onData: (data: T) => void;
  onError: (error: unknown) => void;
}): () => void {
  const handlers = useRef({ read, onData, onError });
  handlers.current = { read, onData, onError };
  const request = useRef<() => void>(() => {});
  const trigger = useCallback(() => request.current(), []);
  useEffect(() => {
    if (!id) { request.current = () => {}; return; }
    let disposed = false, running = false, pending = false, failures = 0;
    let scheduled: ReturnType<typeof setTimeout> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      if (disposed || document.hidden) return;
      if (running) { pending = true; return; }
      running = true;
      clearTimeout(retry);
      try {
        const data = await handlers.current.read(id);
        if (disposed) return;
        handlers.current.onData(data);
        failures = 0;
      } catch (error) {
        if (!disposed) {
          handlers.current.onError(error);
          failures += 1;
          retry = setTimeout(() => { void load(); }, Math.min(16000, 2000 * 2 ** Math.min(failures - 1, 3)));
        }
      } finally {
        running = false;
        if (pending && !disposed) { pending = false; schedule(); }
      }
    };
    // A continuous event stream cannot postpone the first read forever.
    const schedule = () => {
      if (scheduled !== undefined) return;
      scheduled = setTimeout(() => { scheduled = undefined; void load(); }, 250);
    };
    request.current = schedule;
    schedule();
    const interval = setInterval(() => { void load(); }, 20000);
    document.addEventListener('visibilitychange', schedule);
    window.addEventListener('online', schedule);
    window.addEventListener('focus', schedule);
    return () => {
      disposed = true;
      clearTimeout(scheduled); clearTimeout(retry); clearInterval(interval);
      document.removeEventListener('visibilitychange', schedule);
      window.removeEventListener('online', schedule);
      window.removeEventListener('focus', schedule);
      request.current = () => {};
    };
  }, [id, haus]);
  return trigger;
}
